import React, { FunctionComponent, useState } from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { isEqual } from 'lodash-es'
import {
  DataGridFiltersWrapper,
  FieldsWrapper,
  FiltersSlideDown,
  ToolbarToggleWrapper
} from '../../../components/datagrid/filters/filtersStyles'
import { DatePickerField, TextInput } from 'view/components/inputs'
import {
  getTextFlagButtonState,
  TextButton,
  TextButtonType,
  TextFlagButtonsContainer,
  ToolbarTextFlagButton
} from 'view/components/buttons'
import {
  Application,
  AuthorisedUser,
  AxeSubscriptionIdentifier,
  Direction,
  EntityType,
  EntityTypeEnum,
  FilterOperation,
  Platform,
  TradingEntity,
  UserPreference
} from 'api'
import { useChangedSubscriptionFilters } from 'view/shared/entity/hooks/useChangedSubscriptionFilters'
import { createEntitySubscriptionFiltersSelector } from 'selectors/selectionSelectors'
import { createAuthorisedUserSelector } from 'selectors/connectionSelectors'
import { areOnlyDefaultSubscriptionFiltersActive } from 'model'
import { SubscriptionFiltersProps } from '../../../components/datagrid/filters/utils'
import { Locale, Translation } from 'locale'
import { ReversedDirectionSelectboxField } from 'view/shared/entity/ReversedDirectionSelectbox'
import { createUserPreferenceSelector } from 'selectors/userPreferenceSelectors'
import { DefaultFundCombobox } from 'view/shared/entity'
import { AllCounterpartyComboboxField } from 'view/shared/entity/AllCounterpartyOrganisationsCombobox'
import { createFundListByInstrumentSelector } from 'selectors/tradingEntitySelectors'
import { createAllEntitiesSortedSelector } from 'selectors/entitySelectors'

export interface AxeBoardFiltersProps extends SubscriptionFiltersProps {
  subscriptionIdentifier: AxeSubscriptionIdentifier
  authorisedUser: AuthorisedUser
  userPreference: UserPreference
  filteredFundIds: string[]
  allFundsList: TradingEntity[]
  marketDetails: Platform['marketDetails'] | undefined
}

interface AxeBoardFiltersState {
  axeDateAfter: string | null
  axeDateBefore: string | null
  axeId: string | null
  externalAxeId: string | null
  myEntity: string | null
  counterpartyFirm: string | null
  isin: string | null
  bui: string | null
  direction: Direction | null
}

const subscriptionIdentifierSelector = (_: {}, props: AxeBoardFiltersProps) => props.subscriptionIdentifier

const mapStateToProps = createStructuredSelector({
  activeSubscriptionFilters: createEntitySubscriptionFiltersSelector(() => EntityTypeEnum.AXE, subscriptionIdentifierSelector),
  authorisedUser: createAuthorisedUserSelector(),
  userPreference: createUserPreferenceSelector(),
  filteredFundIds: createFundListByInstrumentSelector(),
  allFundsList: createAllEntitiesSortedSelector(EntityType.TRADING_ENTITY)
})

export const AxeBoardFiltersComponent: FunctionComponent<AxeBoardFiltersProps> = ({
  activeSubscriptionFilters,
  subscriptionIdentifier,
  authorisedUser,
  userPreference,
  filteredFundIds,
  allFundsList
}) => {
  const onChangeSubscription = useChangedSubscriptionFilters(EntityTypeEnum.AXE, subscriptionIdentifier, true)
  const isTodayFilterActive = areOnlyDefaultSubscriptionFiltersActive(subscriptionIdentifier, activeSubscriptionFilters, authorisedUser)
  const isInitiator = subscriptionIdentifier === AxeSubscriptionIdentifier.AXE_TODAY_INITIATOR
  const isAdmin = authorisedUser.application === Application.ADMIN_GATEWAY
  const allFundIds = allFundsList.map(fund => fund.id)

  const getInitialSubscriptionFilters = (fieldName: string, filterOperation: FilterOperation) =>
    activeSubscriptionFilters.find(
      subscriptionFilter => subscriptionFilter.fieldName === fieldName && subscriptionFilter.filterOperation === filterOperation
    )?.value ?? null

  const initialSubscriptionFilters: AxeBoardFiltersState = {
    axeDateAfter: isTodayFilterActive ? null : getInitialSubscriptionFilters('createdAt', FilterOperation.AFTER),
    axeDateBefore: isTodayFilterActive ? null : getInitialSubscriptionFilters('createdAt', FilterOperation.BEFORE_OR_EQUAL),
    axeId: isTodayFilterActive ? null : getInitialSubscriptionFilters('axeId', FilterOperation.CONTAINS),
    externalAxeId: isTodayFilterActive ? null : getInitialSubscriptionFilters('externalAxeId', FilterOperation.CONTAINS),
    myEntity: isTodayFilterActive ? null : getInitialSubscriptionFilters('initiatorTradingEntityId', FilterOperation.CONTAINS),
    counterpartyFirm: isTodayFilterActive
      ? null
      : getInitialSubscriptionFilters(isInitiator ? 'responderOrganisationIds' : 'initiatorOrganisationId', FilterOperation.VALUE_CONTAINS),
    isin: isTodayFilterActive ? null : getInitialSubscriptionFilters('collateralId', FilterOperation.CONTAINS),
    bui: isTodayFilterActive ? null : getInitialSubscriptionFilters('collateralBasketId', FilterOperation.CONTAINS),
    direction: isTodayFilterActive ? null : getInitialSubscriptionFilters('direction', FilterOperation.CONTAINS)
  }

  const [subscriptionFilters, setSubscriptionFilters] = useState<AxeBoardFiltersState>(initialSubscriptionFilters)

  const handleSubscribeToday = () => {
    if (!isTodayFilterActive) {
      onChangeSubscription(null)

      setSubscriptionFilters(initialSubscriptionFilters)
    }
  }

  const handleUnsubscribe = () => {
    if (isTodayFilterActive) {
      onChangeSubscription([])
    }
  }

  const handleSubscribeHistorical = () => {
    const filters = []
    if (subscriptionFilters.axeDateAfter) {
      filters.push({
        fieldName: 'createdAt',
        value: subscriptionFilters.axeDateAfter,
        filterOperation: FilterOperation.AFTER,
        orFilter: null,
        andFilter: null,
        calculatedValueType: null
      })
    }
    if (subscriptionFilters.axeDateBefore) {
      filters.push({
        fieldName: 'createdAt',
        value: subscriptionFilters.axeDateBefore,
        filterOperation: FilterOperation.BEFORE_OR_EQUAL,
        orFilter: null,
        andFilter: null,
        calculatedValueType: null
      })
    }
    if (subscriptionFilters.axeId) {
      filters.push({
        fieldName: 'axeId',
        value: subscriptionFilters.axeId,
        filterOperation: FilterOperation.CONTAINS,
        orFilter: null,
        andFilter: null,
        calculatedValueType: null
      })
    }
    if (subscriptionFilters.externalAxeId) {
      filters.push({
        fieldName: 'externalAxeId',
        value: subscriptionFilters.externalAxeId,
        filterOperation: FilterOperation.CONTAINS,
        orFilter: null,
        andFilter: null,
        calculatedValueType: null
      })
    }

    if (subscriptionFilters.counterpartyFirm) {
      filters.push({
        fieldName: isInitiator ? 'responderOrganisationIds' : 'initiatorOrganisationId',
        value: subscriptionFilters.counterpartyFirm,
        filterOperation: isInitiator ? FilterOperation.VALUE_CONTAINS : FilterOperation.CONTAINS,
        orFilter: null,
        andFilter: null,
        calculatedValueType: null
      })
    }
    if (subscriptionFilters.direction) {
      filters.push({
        fieldName: 'direction',
        value: subscriptionFilters.direction,
        filterOperation: FilterOperation.CONTAINS,
        orFilter: null,
        andFilter: null,
        calculatedValueType: null
      })
    }
    if (subscriptionFilters.isin) {
      filters.push({
        fieldName: 'collateralId',
        value: subscriptionFilters.isin,
        filterOperation: FilterOperation.CONTAINS,
        orFilter: null,
        andFilter: null,
        calculatedValueType: null
      })
    }
    if (subscriptionFilters.bui) {
      filters.push({
        fieldName: 'collateralBasketId',
        value: subscriptionFilters.bui,
        filterOperation: FilterOperation.CONTAINS,
        orFilter: null,
        andFilter: null,
        calculatedValueType: null
      })
    }
    if (subscriptionFilters.myEntity) {
      filters.push({
        fieldName: 'initiatorTradingEntityId',
        value: subscriptionFilters.myEntity,
        filterOperation: FilterOperation.CONTAINS,
        orFilter: null,
        andFilter: null,
        calculatedValueType: null
      })
    }
    onChangeSubscription(filters)
  }

  const handleUpdateFilterValue = (key: keyof AxeBoardFiltersState) => (value: string | null) => {
    setSubscriptionFilters(prevSubscriptionFilters => ({ ...prevSubscriptionFilters, [key]: value ? value.trim() : null }))
  }

  return (
    <DataGridFiltersWrapper>
      <ToolbarToggleWrapper>
        <div>{!isTodayFilterActive && Locale.translate(Translation.SC_FILTER_SEARCH_BY)}</div>
        <TextFlagButtonsContainer>
          <ToolbarTextFlagButton
            dataTestId={'AxeBoardFilters-ToolbarTextFlagButton-ButtonToday'}
            state={getTextFlagButtonState(isTodayFilterActive)}
            onClick={handleSubscribeToday}>
            {Locale.translate(Translation.TC_FILTER_TODAYS)}
          </ToolbarTextFlagButton>
          <ToolbarTextFlagButton
            dataTestId={'AxeBoardFilters-ToolbarTextFlagButton-SearchButton'}
            state={getTextFlagButtonState(!isTodayFilterActive)}
            onClick={handleUnsubscribe}>
            {Locale.translate(Translation.TC_FILTER_SEARCH)}
          </ToolbarTextFlagButton>
        </TextFlagButtonsContainer>
      </ToolbarToggleWrapper>
      <FiltersSlideDown closed={isTodayFilterActive}>
        {isTodayFilterActive ? null : (
          <>
            <div>
              <FieldsWrapper>
                <label>{Locale.translate(Translation.SC_FILTER_AXE_AFTER)}</label>
                <DatePickerField
                  label=""
                  fieldName="axeDate"
                  value={subscriptionFilters.axeDateAfter}
                  onUpdate={handleUpdateFilterValue('axeDateAfter')}
                  dataTestId={'AxeBoardFilters-DatePickerField-AxeDateAfter'}
                />
                <label>{Locale.translate(Translation.SC_FILTER_AXE_CREATED_AND_OR_BEFORE)}</label>
                <DatePickerField
                  label=""
                  fieldName="tradeDate"
                  value={subscriptionFilters.axeDateBefore}
                  onUpdate={handleUpdateFilterValue('axeDateBefore')}
                  dataTestId={'AxeBoardFilters-DatePickerField-AxeDateBefore'}
                />
              </FieldsWrapper>
              <FieldsWrapper>
                <label>{Locale.translate(Translation.SC_FILTER_AXE_ID_EQUALS)}</label>
                <TextInput
                  value={subscriptionFilters.axeId}
                  onUpdate={handleUpdateFilterValue('axeId')}
                  dataTestId={'AxeBoardFilters-TextInput-AxeIdEquals'}
                  autoCapitalise={true}
                />
                <label>{Locale.translate(Translation.SC_FILTER_EXTERNAL_AXE_ID_EQUALS)}</label>
                <TextInput
                  value={subscriptionFilters.externalAxeId}
                  onUpdate={handleUpdateFilterValue('externalAxeId')}
                  dataTestId={'AxeBoardFilters-TextInput-ExternalAxeIdEquals'}
                  autoCapitalise={true}
                />
              </FieldsWrapper>
              <FieldsWrapper>
                <label>{Locale.translate(Translation.TC_MY_ENTITY)}</label>
                <DefaultFundCombobox
                  optionKeys={isAdmin ? allFundIds : filteredFundIds}
                  value={subscriptionFilters.myEntity}
                  onUpdate={handleUpdateFilterValue('myEntity')}
                  dataTestId={'AxeBoardFilters-TextInput-MyEntityEquals'}
                  canClear={true}
                />
                <label>{Locale.translate(Translation.TC_COUNTERPARTY_FIRM)}</label>
                <AllCounterpartyComboboxField
                  label={''}
                  value={subscriptionFilters.counterpartyFirm}
                  onUpdate={handleUpdateFilterValue('counterpartyFirm')}
                  dataTestId={'AxeBoardFilters-TextInput-CounterpartyFirmEquals'}
                  canClear={true}
                />
              </FieldsWrapper>
              <FieldsWrapper>
                <label>{Locale.translate(Translation.TC_ISIN)}</label>
                <TextInput
                  value={subscriptionFilters.isin}
                  onUpdate={handleUpdateFilterValue('isin')}
                  dataTestId={'AxeBoardFilters-TextInput-IsinEquals'}
                  autoCapitalise={true}
                />
                <label>{Locale.translate(Translation.TC_BUI)}</label>
                <TextInput
                  value={subscriptionFilters.bui}
                  onUpdate={handleUpdateFilterValue('bui')}
                  dataTestId={'AxeBoardFilters-TextInput-BuiEquals'}
                  autoCapitalise={true}
                />
              </FieldsWrapper>
              <FieldsWrapper>
                <label>{Locale.translate(Translation.SC_FILTER_DIRECTION_EQUALS)}</label>
                <ReversedDirectionSelectboxField
                  label=""
                  dataTestId={'AxeBoardFilters-DirectionSelectbox-DirectionEquals'}
                  canClear={true}
                  directionFormat={userPreference.directionFormat}
                  value={subscriptionFilters.direction}
                  onUpdate={handleUpdateFilterValue('direction')}
                  reversed={!isInitiator}
                />
              </FieldsWrapper>
            </div>
            <TextButton
              dataTestId={'AxeBoardFilters-TextInput-ButtonSearch'}
              type={TextButtonType.Primary}
              disabled={isEqual(initialSubscriptionFilters, subscriptionFilters)}
              onClick={handleSubscribeHistorical}>
              {Locale.translate(Translation.TC_BUTTON_SEARCH)}
            </TextButton>
          </>
        )}
      </FiltersSlideDown>
    </DataGridFiltersWrapper>
  )
}

export const AxeBoardFilters = connect(mapStateToProps)(AxeBoardFiltersComponent)
